import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const OilCooled = () => {
  const features = [
    "Microcontroller voltage regulation",
    "True RMS sensing",
    "±1% output accuracy",
    "Premium FR-4 PCB",
    "Heavy duty steel enclosure",
    "Custom industrial designs",
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-primary-main to-primary-dark text-white py-16" data-testid="oil-cooled-hero">
        <div className="max-w-7xl mx-auto px-6">
          <Button
            asChild
            variant="ghost"
            className="text-white hover:text-white/80 hover:bg-white/10 mb-6 rounded-sm"
            data-testid="back-to-products-button"
          >
            <Link to="/products">
              <ArrowLeft className="mr-2" size={18} />
              Back to Products
            </Link>
          </Button>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4 tracking-tight"
          >
            THREE PHASE OIL COOLED SERVO STABILIZERS
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg font-body max-w-3xl opacity-90"
          >
            Heavy-duty stabilizers for continuous operation with superior thermal efficiency
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white" data-testid="oil-cooled-content">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="https://images.unsplash.com/photo-1671351966453-3f1410780c41?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Oil Cooled Servo Stabilizer"
                className="rounded-sm shadow-lg w-full h-[400px] object-cover"
                data-testid="oil-cooled-image"
              />
            </motion.div>

            {/* Description */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-heading font-bold text-primary-main mb-6">
                DESCRIPTION
              </h2>
              <p className="text-base text-secondary-main font-body leading-relaxed mb-8">
                Designed for heavy industrial loads and continuous operation environments. These stabilizers deliver
                superior thermal efficiency and high reliability.
              </p>

              <h3 className="text-xl font-heading font-bold text-primary-main mb-4">
                FEATURES
              </h3>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`feature-${index}`}>
                    <CheckCircle2 className="text-primary-main flex-shrink-0 mt-0.5" size={20} />
                    <span className="text-base text-secondary-main font-body">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 bg-gray-50" data-testid="oil-cooled-specifications">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-primary-main mb-4">
              CAPACITY RANGE
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white border border-gray-200 rounded-sm p-8 max-w-3xl mx-auto"
            data-testid="specifications-card"
          >
            <div className="space-y-4">
              <div>
                <p className="text-sm font-body font-semibold text-secondary-dark mb-1">Capacity</p>
                <p className="text-lg font-body text-secondary-main">6 KVA – 500 KVA</p>
              </div>
              <div>
                <p className="text-sm font-body font-semibold text-secondary-dark mb-1">Input Voltage</p>
                <p className="text-lg font-body text-secondary-main">345V – 465V / 295V – 470V / 240V – 470V</p>
              </div>
              <div>
                <p className="text-sm font-body font-semibold text-secondary-dark mb-1">Output Voltage</p>
                <p className="text-lg font-body text-secondary-main">380V / 400V / 415V</p>
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button
              asChild
              className="bg-primary-main text-white hover:bg-primary-light rounded-sm px-8 py-6 text-base"
              data-testid="send-enquiry-button"
            >
              <Link to="/contact">Send Enquiry</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OilCooled;
